import { Query } from "@apollo/client/react/components";
import { Loader } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import { ProductSelect } from "@govlaunch/select";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import VendorGovernmentConnectionsQuery from "~/lib/queries/VendorGovernmentConnectionsQuery";
import {
  IVendorGovernmentConnectionsQuery,
  IVendorGovernmentConnectionsQueryVariables,
} from "~/lib/queries/__generated__/VendorGovernmentConnectionsQuery.generated";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import { IProduct } from "~/types/types";
import GovernmentRichLink from "~/components/government/GovernmentRichLink";
import GovernmentIcon from "~/components/icons/GovernmentIcon";
import mediaQueries from "~/components/mediaQueries";
import ProfileBox from "~/components/profile/ProfileBox";
import { ShowMore } from "~/components/profile/ShowMore";
import { Margin } from "~/components/spacings";
import InviteGovernmentsPlaceholder from "~/components/vendor/InviteGovernmentsPlaceholder";
import UpgradeAccountBanner from "~/components/vendor/profile/UpgradeAccountBanner";

interface IVendorGovernmentConnections {
  vendor: NonNullable<IVendorQuery["company"]>;
  showUpgradeBanner?: boolean;
  showPlaceholder?: boolean;
}

export default function VendorGovernmentConnections({
  vendor,
  showUpgradeBanner = true,
  showPlaceholder = true,
}: IVendorGovernmentConnections) {
  const intl = useIntl();
  const [selectedProduct, setSelectedProduct] = useState<Pick<IProduct, "_id" | "name"> | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const { products, slug, viewerIsMember } = vendor;

  if (!vendor.plan.limits.inviteGovernments && vendor.viewerIsMember) {
    return showUpgradeBanner ? <UpgradeAccountBanner /> : null;
  }

  return (
    <div>
      <Query<IVendorGovernmentConnectionsQuery, IVendorGovernmentConnectionsQueryVariables>
        query={VendorGovernmentConnectionsQuery}
        variables={{
          slug: vendor.slug,
          cursor: null,
          product: selectedProduct ? selectedProduct._id : null,
        }}
        notifyOnNetworkStatusChange={true}
      >
        {({ loading, data, fetchMore }) => {
          if (!data) {
            return null;
          }

          const { company: vendor } = data;

          if (!vendor) {
            return null;
          }

          const { governmentConnections } = vendor;

          if (!governmentConnections || !governmentConnections.items || !governmentConnections.pageInfo) {
            return null;
          }

          if (governmentConnections.items.length === 0 && showPlaceholder && !selectedProduct) {
            return viewerIsMember ? <InviteGovernmentsPlaceholder slug={slug} /> : <EmptyGovernmentConnectionsBox />;
          }

          return (
            <div>
              {(products || []).length > 1 && (
                <Margin mb={16}>
                  <ProductSelect
                    value={selectedProduct}
                    css={{
                      minWidth: 300,
                    }}
                    products={products}
                    inputValue={inputValue}
                    onInputValueChange={setInputValue}
                    onChange={setSelectedProduct}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Filter by product', id: '60+BpH',
                    })}
                  />
                </Margin>
              )}

              {governmentConnections.items.length === 0 && selectedProduct && (
                <InviteGovernmentsPlaceholder slug={slug} />
              )}

              {governmentConnections.items.length > 0 && (
                <ProfileBox>
                  <div
                    css={{
                      fontWeight: 500,
                      color: palette.darkGray,
                      fontSize: 16,
                      padding: 16,
                      borderBottom: `1px solid ${palette.lightestGray}`,
                      display: "flex",
                    }}
                  >
                    {selectedProduct ? (
                      <div
                        css={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span css={{ marginRight: 8 }}>{selectedProduct.name} connections</span>
                        <svg width={16} height={16}>
                          <path
                            d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z"
                            fill={palette.greenSuccess}
                            fillRule="nonzero"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        css={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span css={{ marginRight: 8 }}>
                          <FormattedMessage defaultMessage="Verified Governments" id="1cTtuP" /> (
                          {vendor.governmentConnectionsCount})
                        </span>
                        <svg width={16} height={16}>
                          <path
                            d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z"
                            fill={palette.greenSuccess}
                            fillRule="nonzero"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div
                    css={mediaQueries({
                      display: "grid",
                      gridTemplateColumns: ["1fr", "repeat(3, 1fr)"],
                      gridGap: 15,
                      padding: 15,
                    })}
                  >
                    {governmentConnections.items.map((government) => {
                      return <GovernmentRichLink government={government} key={government._id} />;
                    })}
                  </div>

                  {loading && (
                    <div css={{ margin: 24 }}>
                      <Loader centered={true} />
                    </div>
                  )}

                  {!loading && governmentConnections.pageInfo.hasNextPage && (
                    <ShowMore
                      onClick={() => {
                        if (!governmentConnections.pageInfo) {
                          return;
                        }

                        fetchMore({
                          variables: {
                            cursor: governmentConnections.pageInfo.endCursor,
                          },
                          updateQuery: (previousResult, { fetchMoreResult }) => {
                            if (!fetchMoreResult) {
                              return previousResult;
                            }

                            return {
                              __typename: "Query",
                              company: {
                                ...fetchMoreResult.company!,
                                governmentConnections: {
                                  __typename: "Governments",
                                  ...fetchMoreResult.company!.governmentConnections,
                                  items: previousResult.company!.governmentConnections!.items!.concat(
                                    fetchMoreResult.company!.governmentConnections!.items!,
                                  ),
                                  pageInfo: {
                                    ...fetchMoreResult.company!.governmentConnections!.pageInfo!,
                                  },
                                },
                              },
                            };
                          },
                        });
                      }}
                    />
                  )}
                </ProfileBox>
              )}
            </div>
          );
        }}
      </Query>
    </div>
  );
}

function EmptyGovernmentConnectionsBox() {
  return (
    <ProfileBox>
      <div
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 26,
        }}
      >
        <GovernmentIcon width={33} height={33} />
        <Margin ml={14}>
          <span
            css={{
              fontSize: 18,
              fontWeight: 500,
              color: palette.mediumGray,
            }}
          >
            <FormattedMessage defaultMessage="No Government Connections" id="6DydfW" />
          </span>
        </Margin>
      </div>
    </ProfileBox>
  );
}
